import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2 } from 'lucide-react';
import { PlansHierarchy } from './PlansHierarchy'

export const PlansModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    asset_id: '',
    maintenance_type: '',
    priority: '',
    frequency_type: '',
    frequency_value: '',
    duration_minutes: '',
    condition_parameter: '',
    threshold_value: '',
    sensor_asset_id: '',
  });
  const [isHierarchyOpen, setHierarchyOpen] = useState(false);
  const [isHierarchySensorOpen, setHierarchySensorOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [localModalError, setLocalModalError] = useState(null);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoadingAsset, setIsLoadingAsset] = useState(false);
  const [isLoadingSensorAsset, setIsLoadingSensorAsset] = useState(false);
  const [selectedAssetDetails, setSelectedAssetDetails] = useState(null);
  const [selectedSensorAssetDetails, setSelectedSensorAssetDetails] = useState(null);

  const formRef = useRef(null);


  // Initialize form when modal opens (Create mode only)
  useEffect(() => {
    if (isOpen) {
      setLocalModalError(null);
      setFormErrors({});
      // Reset form to default values for Create mode
      setFormData({
        name: '',
        description: '',
        asset_id: '',
        maintenance_type: '',
        priority: '',
        frequency_type: 'days',
        frequency_value: '',
        duration_minutes: '',
        condition_parameter: '',
        threshold_value: '',
        sensor_asset_id: '',
      });
    }
  }, [isOpen]);

  const validateForm = () => {
    console.log('Validating...');
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.asset_id.trim()) {
      errors.asset_id = 'Asset is required';
    }

    if (!formData.priority.trim()) {
      errors.priority = 'Priority is required';
    }

    if (!formData.maintenance_type.trim()) {
      errors.maintenance_type = 'Maintenance type is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (formData.maintenance_type === 'preventive') {
      if (!formData.frequency_value.trim()) {
        errors.frequency_value = 'Frequency value is required';
      } else if (isNaN(formData.frequency_value) || parseInt(formData.frequency_value) <= 0) {
        errors.frequency_value = 'Frequency value must be a positive number';
      }
      if (!formData.frequency_type.trim()) {
        errors.frequency_type = 'Frequency type is required';
      }
    }

    if (formData.maintenance_type === 'predictive') {
      if (!formData.condition_parameter.trim()) {
        errors.condition_parameter = 'Condition parameter is required';
      }
      if (!formData.threshold_value.trim()) {
        errors.threshold_value = 'Threshold value is required';
      } else if (isNaN(formData.threshold_value) || parseFloat(formData.threshold_value) <= 0) {
        errors.threshold_value = 'Threshold value must be a positive number';
      } else if (formData.threshold_value.toString().split('.')[1]?.length > 4) {
        errors.threshold_value = 'Threshold value must not be greater than 4 decimal places';
      }
      if (!formData.sensor_asset_id.trim()) {
        errors.sensor_asset_id = 'Sensor asset is required';
      }
    }

    if (formData.maintenance_type === 'predictive' || formData.maintenance_type === 'preventive') {
      if (!formData.duration_minutes.trim()) {
        errors.duration_minutes = 'Duration is required';
      } else if (isNaN(formData.duration_minutes) || parseInt(formData.duration_minutes) <= 0) {
        errors.duration_minutes = 'Duration must be a positive number';
      }
    }

    console.log('Validation errors:', errors);
    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };
  {/* Activated when Save is pressed */ }
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLocalIsSubmitting(true);
    setLocalModalError(null);

    try {
      const productData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        asset_id: parseInt(formData.asset_id.trim()),
        maintenance_type: formData.maintenance_type.trim(),
        priority: formData.priority.trim(),
        frequency_type: formData.maintenance_type.trim() === 'preventive' ? formData.frequency_type.trim() : 'days', //Default to 'days' for non-preventive plans
        frequency_value: formData.maintenance_type.trim() === 'preventive' ? parseInt(formData.frequency_value.trim()) : 1, //Default to 1 for non-preventive plans
        duration_minutes: parseInt(formData.duration_minutes.trim()),
        condition_parameter: formData.maintenance_type.trim() === 'predictive' ? formData.condition_parameter.trim() : null,
        threshold_value: formData.maintenance_type.trim() === 'predictive' ? parseFloat(formData.threshold_value.trim()) : null,
        sensor_asset_id: formData.maintenance_type.trim() === 'predictive' ? parseInt(formData.sensor_asset_id.trim()) : null,
      };

      console.log('Product data to save:', productData);
      await onSave(productData);
      onClose();
    } catch (error) {
      setLocalModalError(error.message || 'Error saving plan');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      //Limit the number of characters for each field
      [name]: name === 'name' ? value.slice(0, 255) : name === 'condition_parameter' ? value.slice(0, 100) : value
    }));
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  {/* Set into Form  asset and sensor_asset selected from the Asset Hierarchy */ }
  useEffect(() => {
    setIsUpdating(true);
    formData.asset_id === '' ? setFormData(prev => ({
      ...prev,
      asset_id: selectedAssetDetails?.ID.toString() || '',
    }))
      : setFormData(prev => ({
        ...prev,
        asset_id: selectedAssetDetails?.ID.toString() || formData.asset_id,
      }))

  }, [selectedAssetDetails])

  useEffect(() => {
    setIsUpdating(true);
    formData.sensor_asset_id === '' ? setFormData(prev => ({
      ...prev,
      sensor_asset_id: selectedSensorAssetDetails?.ID.toString() || '',
    }))
      : setFormData(prev => ({
        ...prev,
        sensor_asset_id: selectedSensorAssetDetails?.ID.toString() || formData.sensor_asset_id,
      }))

  }, [selectedSensorAssetDetails])


  useEffect(() => {
    formData.asset_id !== '' ? setIsUpdating(false) : null;
  }, [formData.asset_id])

  useEffect(() => {
    formData.sensor_asset_id !== '' ? setIsUpdating(false) : null;
  }, [formData.sensor_asset_id])

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center gap-3 justify-center p-4 bg-black/60 backdrop-blur-sm border border-white">
      <div className={(isHierarchyOpen || isHierarchySensorOpen) ? 'visible' : 'invisible'}>
        <PlansHierarchy
          assetId={selectedAsset}
          setAssetId={setSelectedAsset}
          setIsLoadingAsset={setIsLoadingAsset}
          setIsLoadingSensorAsset={setIsLoadingSensorAsset}
          setAssetDetails={(isHierarchyOpen && !isHierarchySensorOpen) ? setSelectedAssetDetails : (!isHierarchyOpen && isHierarchySensorOpen) ? setSelectedSensorAssetDetails : setSelectedAssetDetails}
          selectMode={(isHierarchyOpen && !isHierarchySensorOpen) ? 'asset' : (!isHierarchyOpen && isHierarchySensorOpen) ? 'sensor' : null}
        />
      </div>
      <div className={`bg-gray-800 ${(!isHierarchyOpen && !isHierarchySensorOpen) ? 'absolute left-1/2 -translate-x-1/2' : ''} rounded-2xl border border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex-1 flex flex-col`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            Create Plan
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* General error message */}
            {localModalError && (
              <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                <p className="text-red-300 text-sm">{localModalError}</p>
              </div>
            )}

            {/* Basic Information Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                Basic Information
              </h3>

              {/* Name and Product Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    maxlength="255"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.name ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="Plan name"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Priority <span className="text-red-400">*</span>
                  </label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formData.priority ? 'text-white' : 'text-gray-400'} ${formErrors.priority ? 'border-red-500' : 'border-gray-600'}
                            focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-600`}>
                    <option value="" disabled selected>Select a priority</option>
                    <option value="low" className='text-white'>Low</option>
                    <option value="medium" className='text-white'>Medium</option>
                    <option value="high" className='text-white'>High</option>
                    <option value="critical" className='text-white'>Critical</option>
                  </select>
                  {formErrors.priority && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.priority}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Asset <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-row gap-2">
                    <p className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formErrors.asset_id ? 'border-red-500' : 'border-gray-600'} ${formData.asset_id ? 'text-white' : 'text-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-600`}>
                      {selectedAssetDetails?.Location ? `${selectedAssetDetails.Location}` : formData.asset_id === '' ? 'Not selected' : (<Loader2 className="w-4 h-4 animate-spin" />)}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        isHierarchySensorOpen ? setHierarchySensorOpen(false) : null;
                        isHierarchyOpen ? setHierarchyOpen(false) : setHierarchyOpen(true);

                      }}
                      className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"

                    >
                      {isHierarchyOpen ? `Close` : `Open`}
                    </button>
                  </div>
                  {formErrors.asset_id && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.asset_id}</p>
                  )}
                </div>

                {/* Manufacturer and Family */}
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Maintenance Type <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="maintenance_type"
                      value={formData.maintenance_type}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formErrors.maintenance_type ? 'border-red-500' : 'border-gray-600'} ${formData.maintenance_type ? 'text-white' : 'text-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-600`}>
                      <option value="" disabled selected>Select a maintenance type</option>
                      <option value="preventive" className='text-white'>Preventive</option>
                      <option value="predictive" className='text-white'>Predictive</option>
                      <option value="reactive" className='text-white'>Reactive</option>
                    </select>
                    {formErrors.maintenance_type && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.maintenance_type}</p>
                    )}
                  </div>
                </div>

              </div>

              {/* Description */}
              <div className="border-b border-gray-700 pb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-white ${formErrors.description ? 'border-red-500' : 'border-gray-600'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                  placeholder="Product description"
                  disabled={localIsSubmitting}
                />
                {formErrors.description && (
                  <p className="mt-1 text-sm text-red-400">{formErrors.description}</p>
                )}
              </div>
            </div>

            {/* Preventive Fields */}
            {formData.maintenance_type === 'preventive' && (
              <div className="space-y-4">

                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Frequency <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-1 sm:flex sm:justify-items-start gap-2">
                  <p className="block text-base font-normal text-gray-100 mt-2">Execute plan every </p>
                  <div>
                    <input
                      type="number"
                      name="frequency_value"
                      value={formData.frequency_value}
                      min="1"
                      max={Number.MAX_SAFE_INTEGER}
                      onChange={handleChange}
                      className={`px-4 py-2 w-full sm:w-auto rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.frequency_value ? 'border-red-500' : 'border-gray-600'
                        }`}
                      placeholder="1~9999"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.frequency_value && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.frequency_value}</p>
                    )}
                  </div>
                  <div>
                    <select
                      name="frequency_type"
                      value={formData.frequency_type}
                      onChange={handleChange}
                      className={`px-4 py-2 mt-2 sm:mt-0 sm:ml-0 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.frequency_type ? 'border-red-500' : 'border-gray-600'
                        }`}
                      disabled={localIsSubmitting}
                    >
                      <option value="days" className='text-white'>days</option>
                      <option value="weeks" className='text-white'>weeks</option>
                      <option value="months" className='text-white'>months</option>
                      <option value="hours" className='text-white'>hours</option>
                      <option value="uses" className='text-white'>uses</option>
                    </select>
                    {formErrors.frequency_value && (
                      <p className="mt-1 text-sm text-red-400"></p>
                    )}
                  </div>


                </div>

              </div>
            )}
            {formData.maintenance_type === 'predictive' && (
              <div className="space-y-4">

                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Condition <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-1 sm:flex sm:justify-items-start gap-2">
                  <p className="block text-base font-normal text-gray-100 mt-2">If </p>
                  <div>
                    <input
                      type="text"
                      name="condition_parameter"
                      maxlength="100"
                      value={formData.condition_parameter}
                      onChange={handleChange}
                      className={`px-4 py-2 w-full sm:w-auto rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.condition_parameter ? 'border-red-500' : 'border-gray-600'
                        }`}
                      placeholder="i.e. vibration"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.condition_parameter && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.condition_parameter}</p>
                    )}
                  </div>

                  <div>
                    <select
                      name="operation"
                      //value={formData.operation}
                      //onChange={handleChange}
                      className={`px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.operation ? 'border-red-500' : 'border-gray-600'
                        }`}
                      disabled={localIsSubmitting}
                    >
                      <option value="less" className='text-white'>&lt;</option>
                      <option value="greater" className='text-white'>&gt;</option>
                      <option value="equal" className='text-white'>=</option>
                    </select>
                    <p className="mt-1 text-sm text-red-400"></p>
                  </div>
                  <div>
                    <input
                      type="number"
                      name="threshold_value"
                      value={formData.threshold_value}
                      onChange={handleChange}
                      min="0"
                      step='0.0001'
                      max={999999.9999}
                      className={`px-4 py-2 w-full sm:w-auto rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.threshold_value ? 'border-red-500' : 'border-gray-600'
                        }`}
                      placeholder="0.0001"
                      disabled={localIsSubmitting}
                    />
                    {formErrors.threshold_value && (
                      <p className="mt-1 text-sm text-red-400">{formErrors.threshold_value}</p>
                    )}
                  </div>



                </div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sensor Asset <span className="text-red-400">*</span>
                </label>
                <div>
                  <div className="flex flex-row gap-2">
                    <p className={`w-full px-4 py-2 rounded-lg border bg-gray-700 ${formErrors.sensor_asset_id ? 'border-red-500' : 'border-gray-600'} ${formData.sensor_asset_id ? 'text-white' : 'text-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-600`}>
                      {selectedSensorAssetDetails?.Product?.Name ? `${selectedSensorAssetDetails.Product.Name}` : formData.sensor_asset_id === '' ? 'Not selected' : (<Loader2 className="w-4 h-4 animate-spin" />)}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        isHierarchyOpen ? setHierarchyOpen(false) : null;
                        isHierarchySensorOpen ? setHierarchySensorOpen(false) : setHierarchySensorOpen(true);
                      }}
                      className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={localIsSubmitting}
                    >
                      {isHierarchySensorOpen ? `Close` : `Open`}
                    </button>
                  </div>
                  {formErrors.sensor_asset_id && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.sensor_asset_id}</p>
                  )}
                </div>

              </div>

            )}
            {(formData.maintenance_type === 'preventive' || formData.maintenance_type === 'predictive') && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Estimated Duration <span className="text-red-400">*</span>
                </label>
                <div className="flex justify-between sm:justify-start gap-2">

                  <input
                    type="number"
                    name="duration_minutes"
                    value={formData.duration_minutes}
                    min="1"
                    max={Number.MAX_SAFE_INTEGER}
                    onChange={handleChange}
                    className={`w-2/3 sm:w-auto px-4 py-2 rounded-lg border bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${formErrors.duration_minutes ? 'border-red-500' : 'border-gray-600'
                      }`}
                    placeholder="1~9999"
                    disabled={localIsSubmitting}
                  />
                  <p className="w-1/3 sm:w-auto block text-base font-normal text-gray-100 mt-2">minutes</p>
                </div>
                {formErrors.duration_minutes && (
                  <p className="mt-1 text-sm text-red-400">{formErrors.duration_minutes}</p>
                )}
              </div>
            )}
          </div>

          {/* Footer with buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            {/* Cancel Button */}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={localIsSubmitting}
            >
              Cancel
            </button>
            {/* Save Button */}
            <button
              type="submit"
              className="px-4 py-2 rounded-lg border border-blue-600 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={localIsSubmitting || isUpdating}
            >
              {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Save
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};
